<script setup>
import { computed, ref, watch, useId } from 'vue'
import { useI18n } from 'vue-i18n'
import { useModalFocus } from '@/composables/useModalFocus.js'
import { useAuthStore } from '@/stores/auth'
import { saveFrontpageCard, getMacros } from '@/api/api'
import { FRONTPAGE_ICONS, resolveFrontpageIcon } from '@/utils/frontpageIcons'
import { FRONTPAGE_COLORS, resolveFrontpageColor } from '@/utils/frontpageColors'
import { FRONTPAGE_PRESETS } from '@/utils/frontpagePresets'
import MdiPencil from '~icons/mdi/pencil'
import MdiChevronLeft from '~icons/mdi/chevron-left'
import BaseFormField from '@/components/base/BaseFormField.vue'
import BaseInput from '@/components/base/BaseInput.vue'
import BaseSelect from '@/components/base/BaseSelect.vue'
import BaseFormActions from '@/components/base/BaseFormActions.vue'

const props = defineProps({
  show: { type: Boolean, required: true },
  card: { type: Object, default: null },
})

const emit = defineEmits(['saved', 'cancel'])
const { t } = useI18n()
const auth = useAuthStore()
const dialog = ref(null)
const id = useId()
const busy = ref(false)
const error = ref('')
const macros = ref([])
const metrics = ['item', 'item_metadata', 'location', 'warehouse', 'customer', 'project', 'area']
/** 'pick' | 'form' — pick only when creating */
const step = ref('form')
const draft = ref(null)

const isCreate = computed(() => !props.card?.id)

function blankDraft() {
  return {
    title: '',
    section: t('home.overview'),
    order: 10,
    col_span: 1,
    content_type: 'link',
    data: '',
    link: '',
    icon: 'package-variant',
    color: 'sky',
    macro: null,
  }
}

watch(
  () => [props.show, props.card],
  async ([show]) => {
    if (!show) {
      draft.value = null
      error.value = ''
      step.value = 'form'
      return
    }
    try {
      const result = await getMacros()
      macros.value = result.results ?? result
    } catch {
      macros.value = []
    }
    if (isCreate.value) {
      draft.value = blankDraft()
      step.value = 'pick'
    } else {
      const source = props.card || {}
      const knownIcon = source.icon && FRONTPAGE_ICONS.some((i) => i.id === source.icon)
      let icon = 'package-variant'
      if (knownIcon) icon = source.icon
      else if (source.content_type === 'macro') icon = 'play'
      else if (source.content_type === 'chart') icon = 'chart'
      else if (source.content_type === 'statistics') icon = 'cube'
      draft.value = {
        ...blankDraft(),
        ...source,
        icon,
        color: resolveFrontpageColor(source.color, 'sky'),
        macro: source.macro?.id ?? source.macro ?? null,
      }
      step.value = 'form'
    }
    error.value = ''
  },
  { immediate: true },
)

const cancel = () => {
  if (!busy.value) emit('cancel')
}
useModalFocus(dialog, () => props.show, cancel)

const modalTitle = computed(() => {
  if (!isCreate.value) return t('common.edit')
  if (step.value === 'pick') return t('ui.addCard')
  return t('ui.customCard')
})

function openCustom() {
  draft.value = blankDraft()
  step.value = 'form'
  error.value = ''
}

function backToPick() {
  if (busy.value) return
  step.value = 'pick'
  error.value = ''
}

async function applyPreset(preset) {
  if (busy.value) return
  draft.value = { ...blankDraft(), ...preset.card }
  busy.value = true
  error.value = ''
  try {
    await persist(draft.value)
    emit('saved')
  } catch {
    error.value = t('ui.couldNotSaveTheCardCheckTheFieldsAndTryAgain')
    step.value = 'form'
  } finally {
    busy.value = false
  }
}

async function persist(values) {
  const { id: cardId, title, section, order, col_span, content_type, data, link, macro, icon, color } = values
  await saveFrontpageCard({
    id: cardId,
    title,
    section,
    order,
    col_span,
    content_type,
    data,
    link,
    icon,
    color,
    macro: macro || null,
    tenant: auth.currentTenant.id,
  })
}

async function save() {
  if (!draft.value) return
  busy.value = true
  error.value = ''
  try {
    await persist(draft.value)
    emit('saved')
  } catch {
    error.value = t('ui.couldNotSaveTheCardCheckTheFieldsAndTryAgain')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <div v-if="show && draft" class="modal-overlay" @click.self="cancel">
      <div
        ref="dialog"
        class="confirm-modal fp-edit-modal"
        role="dialog"
        aria-modal="true"
        tabindex="-1"
        :aria-labelledby="`${id}-title`"
        :aria-busy="busy"
      >
        <div class="fp-edit-modal__top">
          <button
            v-if="isCreate && step === 'form'"
            type="button"
            class="btn-icon"
            :disabled="busy"
            :aria-label="$t('common.back')"
            @click="backToPick"
          >
            <MdiChevronLeft class="w-5 h-5" />
          </button>
          <h2 :id="`${id}-title`" class="confirm-modal__title" style="margin-bottom: 0;">
            {{ modalTitle }}
          </h2>
        </div>
        <p class="form-hint mb-3">{{ $t('ui.changesApplyToEveryoneInThisTenantLowerOrderNumbersAppearFirst') }}</p>
        <p v-if="error" role="alert" class="state-empty error" style="padding: 0.5rem 0;">{{ error }}</p>

        <!-- Preset picker (create only) -->
        <div v-if="step === 'pick'" class="fp-preset-grid">
          <button
            v-for="preset in FRONTPAGE_PRESETS"
            :key="preset.id"
            type="button"
            class="fp-preset"
            :disabled="busy"
            @click="applyPreset(preset)"
          >
            <span class="fp-tile__chip" :class="`fp-accent--${preset.color}`">
              <component :is="resolveFrontpageIcon(preset.icon)" class="fp-tile__chip-icon" />
            </span>
            <span class="fp-preset__text">
              <span class="fp-preset__title">{{ $t(preset.titleKey) }}</span>
              <span class="fp-preset__hint">{{ $t(preset.hintKey) }}</span>
            </span>
          </button>
          <button type="button" class="fp-preset fp-preset--custom" :disabled="busy" @click="openCustom">
            <span class="fp-tile__chip fp-accent--gray">
              <MdiPencil class="fp-tile__chip-icon" />
            </span>
            <span class="fp-preset__text">
              <span class="fp-preset__title">{{ $t('ui.customCard') }}</span>
              <span class="fp-preset__hint">{{ $t('ui.customCardHint') }}</span>
            </span>
          </button>
          <div class="confirm-modal__actions" style="grid-column: 1 / -1;">
            <button type="button" class="confirm-btn cancel" :disabled="busy" @click="cancel">
              {{ $t('common.cancel') }}
            </button>
          </div>
        </div>

        <!-- Full form (edit or custom create) -->
        <form v-else class="list-stack" style="padding: 0;" @submit.prevent="save">
          <BaseFormField :label="$t('maintenance.form.title')" required>
            <BaseInput v-model="draft.title" required maxlength="200" />
          </BaseFormField>
          <BaseFormField :label="$t('projectItems.section')">
            <BaseInput v-model="draft.section" maxlength="100" />
          </BaseFormField>
          <BaseFormField :label="$t('ui.order')" required>
            <BaseInput v-model.number="draft.order" type="number" required />
          </BaseFormField>
          <BaseFormField :label="$t('ui.size')">
            <BaseSelect v-model.number="draft.col_span">
              <option :value="1">{{ $t('ui.normal') }}</option>
              <option :value="2">{{ $t('ui.wide') }}</option>
            </BaseSelect>
          </BaseFormField>
          <BaseFormField :label="$t('ui.cardType')">
            <BaseSelect v-model="draft.content_type">
              <option value="link">{{ $t('ui.shortcut') }}</option>
              <option value="statistics">{{ $t('ui.statistic') }}</option>
              <option value="chart">{{ $t('ui.chart') }}</option>
              <option value="macro">{{ $t('ui.macro') }}</option>
            </BaseSelect>
          </BaseFormField>
          <BaseFormField v-if="draft.content_type === 'statistics'" :label="$t('ui.metric')" required>
            <BaseSelect v-model="draft.data" required>
              <option
                v-for="metric in metrics"
                :key="metric"
                :value="metric"
              >{{ $t(`bulkEdit.resources.${metric === 'item_metadata' ? 'instances' : metric + 's'}`) }}</option>
            </BaseSelect>
          </BaseFormField>
          <BaseFormField v-if="draft.content_type === 'chart'" :label="$t('ui.chart')" required>
            <BaseSelect v-model="draft.data" required>
              <option value="readiness">{{ $t('ui.equipmentReadiness') }}</option>
              <option value="workload">{{ $t('ui.upcomingWorkload') }}</option>
            </BaseSelect>
          </BaseFormField>
          <BaseFormField v-if="draft.content_type === 'macro'" :label="$t('ui.macroId')" required>
            <BaseSelect v-model.number="draft.macro" required>
              <option v-for="macro in macros" :key="macro.id" :value="macro.id">{{ macro.name }}</option>
            </BaseSelect>
          </BaseFormField>
          <BaseFormField :label="$t('transfers.destinationLabel')">
            <BaseInput v-model="draft.link" maxlength="200" placeholder="/items" />
          </BaseFormField>

          <fieldset class="form-field">
            <legend class="form-label">{{ $t('ui.icon') }}</legend>
            <div class="fp-picker">
              <button
                v-for="item in FRONTPAGE_ICONS"
                :key="item.id"
                type="button"
                class="fp-picker__swatch"
                :class="{ 'fp-picker__swatch--active': draft.icon === item.id }"
                :aria-label="item.id"
                :aria-pressed="draft.icon === item.id"
                @click="draft.icon = item.id"
              >
                <component :is="resolveFrontpageIcon(item.id)" class="w-5 h-5" />
              </button>
            </div>
          </fieldset>

          <fieldset class="form-field">
            <legend class="form-label">{{ $t('ui.color') }}</legend>
            <div class="fp-picker">
              <button
                v-for="item in FRONTPAGE_COLORS"
                :key="item.id"
                type="button"
                class="fp-picker__swatch fp-picker__color"
                :class="[`fp-accent--${item.id}`, { 'fp-picker__swatch--active': draft.color === item.id }]"
                :aria-label="$t(item.labelKey)"
                :aria-pressed="draft.color === item.id"
                @click="draft.color = item.id"
              />
            </div>
          </fieldset>

          <BaseFormActions
            :confirm-label="$t('ui.saveCard')"
            :loading="busy"
            @cancel="cancel"
          />
        </form>
      </div>
    </div>
  </Teleport>
</template>
